import type { Action, Id, KoRound, KoRoundsConfig, Match, MatchStatus, Phase, Tournament } from '../../types.js';
import { createRng } from '../../rng.js';
import { nextId } from '../../ids.js';
import { walkoverResult } from '../../rules/validate.js';
import { EngineError } from '../state.js';
import { phaseOf, rulesOf, teamOf } from './shared.js';
import { renumber } from './phases.js';
import { finishMatch } from './matches.js';

// ---------------------------------------------------------------------------
// KO-Runden (docs/notes/ko-runden.md)
// ---------------------------------------------------------------------------

export function applyKo(t: Tournament, action: Action): string[] {
  const warnings: string[] = [];
  switch (action.type) {
    case 'ko.startRound':
      warnings.push(...startKoRound(t, action.phaseId, action.teamIds, { label: action.label, courtGroup: action.courtGroup, pairing: action.pairing }, action.at));
      break;
  }
  return warnings;
}

const KO_OPEN: ReadonlySet<MatchStatus> = new Set<MatchStatus>(['open', 'ready', 'running']);

/** Läuft in dieser Runde noch ein Spiel? */
function koRoundOpen(phase: Phase, round: KoRound): boolean {
  return round.matchIds.some((id) => {
    const m = phase.matches.find((x) => x.id === id);
    return !!m && KO_OPEN.has(m.status);
  });
}

/** Paarungen einer Runde nach Setzreihenfolge (`order` ohne Freilos-Team) */
function koPairs(order: readonly Id[], pairing: 'cross' | 'seeded' | 'random', seed: string): [Id, Id][] {
  const list = pairing === 'random' ? createRng(seed).shuffle(order) : order.slice();
  const pairs: [Id, Id][] = [];
  if (pairing === 'cross') {
    for (let i = 0; i < list.length / 2; i++) pairs.push([list[i] as Id, list[list.length - 1 - i] as Id]);
  } else {
    for (let i = 0; i + 1 < list.length; i += 2) pairs.push([list[i] as Id, list[i + 1] as Id]);
  }
  return pairs;
}

/**
 * Nächste KO-Runde von Hand starten (docs/notes/ko-runden.md). Die
 * Turnierleitung entscheidet vor jeder Runde nach Uhrzeit und Lage, wie viele
 * Teams weiterspielen; die Engine paart nur noch und legt die Spiele an. Nach
 * dem Ende einer Runde passiert nichts automatisch.
 */
function startKoRound(
  t: Tournament,
  phaseId: Id,
  teamIds: readonly Id[],
  opts: { label?: string | undefined; courtGroup?: string | undefined; pairing?: 'cross' | 'seeded' | 'random' | undefined },
  at: number,
): string[] {
  const { comp, phase } = phaseOf(t, phaseId);
  if (phase.type !== 'ko_rounds') throw new EngineError('Diese Phase spielt keine KO-Runden');
  if (phase.status === 'finished') throw new EngineError('Phase ist abgeschlossen');
  const cfg = phase.config as KoRoundsConfig;
  const warnings: string[] = [];
  if (!phase.koRounds) phase.koRounds = [];
  const rounds = phase.koRounds;
  const courtGroup = opts.courtGroup?.trim() || cfg.courtGroup;

  // Eine laufende Runde blockiert die nächste. Zwei Runden auf verschiedenen
  // Plätzen laufen parallel (Ort 1 und Ort 2, docs/17) und blockieren sich
  // nicht; eine Runde ohne Platz wartet auf alle.
  const blocking = rounds.some((r) => {
    if (courtGroup && r.courtGroup && r.courtGroup !== courtGroup) return false;
    return koRoundOpen(phase, r);
  });
  if (blocking) throw new EngineError('Erst die laufende Runde beenden');

  if (teamIds.length < 2) throw new EngineError('Mindestens 2 Teams für eine KO-Runde');
  const seen = new Set<Id>();
  for (const id of teamIds) {
    const team = teamOf(comp, id);
    if (seen.has(id)) throw new EngineError(`${team.name} steht doppelt in der Liste`);
    seen.add(id);
    if (team.status === 'withdrawn' || team.status === 'disqualified') throw new EngineError(`${team.name} ist zurückgezogen`);
  }

  const number = rounds.length + 1;
  const entrants = teamIds.slice();
  const pairing = opts.pairing ?? cfg.pairing;
  // Ungerade Anzahl: der Bestgesetzte bekommt ein Freilos (wie im Bracket, docs/04 §6)
  const bye = entrants.length % 2 === 1 ? (entrants[0] as Id) : undefined;
  const pairs = koPairs(bye ? entrants.slice(1) : entrants, pairing, `${t.seed}:${phase.id}:ko:${number}`);

  const matchIds: Id[] = [];
  const newMatch = (a: Match['a'], b: Match['b']): Match => {
    const m: Match = {
      id: nextId(t, `m:${phase.id}`),
      phaseId: phase.id,
      round: number,
      number: 0,
      a,
      b,
      status: 'ready',
      priority: 0,
      bracketRole: 'main',
    };
    phase.matches.push(m);
    matchIds.push(m.id);
    return m;
  };
  const byeMatch = bye ? newMatch({ kind: 'team', teamId: bye }, { kind: 'bye' }) : undefined;
  for (const [a, b] of pairs) newMatch({ kind: 'team', teamId: a }, { kind: 'team', teamId: b });

  const round: KoRound = { id: nextId(t, `kr:${phase.id}`), number, entrants, matchIds };
  if (courtGroup) round.courtGroup = courtGroup;
  const label = opts.label?.trim();
  if (label) round.label = label;
  rounds.push(round);
  renumber(phase);

  phase.status = 'running';
  if (comp.status === 'setup') comp.status = 'running';
  if (t.status === 'draft') t.status = 'running';

  if (byeMatch) {
    // Freilos wie im Bracket: das Spiel ist sofort gewertet, das Team steht in der nächsten Runde
    finishMatch(t, comp, phase, byeMatch, walkoverResult(rulesOf(comp, phase), 'A'), 'walkover', at);
    warnings.push(`Freilos für ${teamOf(comp, bye as Id).name}`);
  }
  warnings.push(`Runde ${number}: ${entrants.length} Teams, ${matchIds.length} Spiel(e)${courtGroup ? ` auf Platz „${courtGroup}"` : ''}`);
  return warnings;
}
